<template>
  <div class="flex flex-col w-full items-center relative">
    <p class="text-lg md:text-3xl lg:text-4xl mb-1 md:pb-4 text-[#061B44] text-center font-bold">
      {{ $t("audio-transcription") }}
    </p>
    <p
      class="text-xs md:text-sm lg:text-base pb-2 text-[#061B44] text-center font-medium w-[85%] md:w-[70%] lg:w-[40%]"
    >
      {{
        $t(
          "transcribe-audio-to-text-with-our-ai-audio-to-text-transcription-tool-120-languages-and-45-formats-supported"
        )
      }}
    </p>
    <div class="w-full flex flex-col justify-center items-center">
      <div class="w-full flex flex-col items-center">
        <!-- reproces new video button -->
        <button
          v-if="(processingStarted && receivedCaption) || isFromNotification"
          @click="showNewProcessPop = true"
          class="min-w-[60%] lg:min-w-[25%] flex justify-center items-center space-x-1 lg:space-x-4 bg-subDark h-10 p-2 lg:p-3 rounded-lg"
        >
          <svg
            clip-rule="evenodd"
            fill-rule="evenodd"
            stroke-linejoin="round"
            class="h-6 w-6 lg:h-6 fill-white"
          >
            <path
              d="m3.508 6.726c1.765-2.836 4.911-4.726 8.495-4.726 5.518 0 9.997 4.48 9.997 9.997 0 5.519-4.479 9.999-9.997 9.999-5.245 0-9.553-4.048-9.966-9.188-.024-.302.189-.811.749-.811.391 0 .715.3.747.69.351 4.369 4.012 7.809 8.47 7.809 4.69 0 8.497-3.808 8.497-8.499 0-4.689-3.807-8.497-8.497-8.497-3.037 0-5.704 1.597-7.206 3.995l1.991.005c.414 0 .75.336.75.75s-.336.75-.75.75h-4.033c-.414 0-.75-.336-.75-.75v-4.049c0-.414.336-.75.75-.75s.75.335.75.75z"
              fill-rule="nonzero"
            />
          </svg>
          <p 
          :class="{'pr-4': locale === 'ar'}"
          class="text-white font-semibold text-sm lg:text-lg">
            {{ $t("Start_a_New_Transcription") }}
          </p>
        </button>
        <div v-if="showNewProcessPop" class="overlay z-50">
          <div class="overlay-content flex flex-col w-[85%] md:w-[75%] lg:w-[55%] xl:w-[45%] 2xl:w-[30%] space-y-4" @click.stop>
            <i class="pi pi-exclamation-circle text-[#061B44] text-4xl text-center"></i>
            <p class="text-[#061B44] font-semibold text-lg pb-2 text-center">
              {{ $t("Starting_a_new_transcription_will") }}
            </p>
            <div class="flex flex-row space-x-2 justify-between">
              <button
                @click="showNewProcessPop = false"
                class="bg-white border border-slate-900 rounded-md text-slate-900 text-lg font-bold py-1 px-8 h-10"
              >
                {{ $t("No_Stay_on_Page") }}
              </button>
              <button
                @click="
                  () => {
                    showNewProcessPop = false;
                    processNewAudio();
                  }
                "
                class="bg-subDark border border-slate-900 rounded-md text-white text-lg font-bold py-1 px-8 h-10"
              >
                {{ $t("Yes_Start_New") }}
              </button>
            </div>
          </div>
        </div>
        <ProcessAudioFile ref="processAudioFileRef" />
        <!-- <AudioProcessingResult /> -->
      </div>
    </div>
    <button
      @click="showHelp = !showHelp"
      class="fixed z-10 right-0 bottom-1/3 bg-slate-900 rounded-l-full px-5 h-10 text-white font-semibold"
    >
      <div class="flex gap-3 justify-center items-center">
        <span>
          <svg
            class="w-6 h-6 text-white fill-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
          >
            <path
              d="M12 1c-6.338 0-12 4.226-12 10.007 0 2.05.738 4.063 2.047 5.625.055 1.83-1.023 4.456-1.993 6.368 2.602-.47 6.301-1.508 7.978-2.536 9.236 2.247 15.968-3.405 15.968-9.457 0-5.812-5.701-10.007-12-10.007zm0 15c-.565 0-1.024-.459-1.024-1.025 0-.565.459-1.024 1.024-1.024.566 0 1.024.459 1.024 1.024 0 .566-.458 1.025-1.024 1.025zm1.606-4.858c-.74.799-.775 1.241-.766 1.785h-1.643c-.006-1.208.016-1.742 1.173-2.842.469-.446.84-.799.788-1.493-.047-.66-.599-1.004-1.117-1.004-.581 0-1.261.432-1.261 1.649h-1.646c0-1.966 1.155-3.237 2.941-3.237.849 0 1.592.278 2.09.783.468.473.709 1.125.7 1.883-.013 1.134-.704 1.878-1.259 2.476z"
            />
          </svg>
        </span>
        <span class="text-base font-semibold">{{ $t('help') }}</span>
      </div>
    </button>
    <!-- tutorial overlay -->
    <div
      v-if="showHelp"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 overflow-y-auto"
      @click="showHelp = false"
    >
      <div
        class="bg-white rounded-lg shadow-lg p-6 w-[90%] md:w-[70%] lg:w-[50%] max-w-3xl mx-auto h-[70%] sm:max-h-max relative"
        @click.stop
      >
        <h2 class="text-sm md:text-lg font-semibold text-center text-[#061B44] mb-6 mt-10">
          {{ translated.title }}
        </h2>
        <div class="grid md:grid-cols-2 gap-4 overflow-y-scroll h-[78%]">
          <div
            v-for="(feature, index) in translated.features"
            :key="index"
            :class="locale === 'ar' ? 'space-x-reverse' : ''"
            class="flex items-start space-x-4 bg-gray-100 p-4 rounded-lg"
          >
            <div
              class="min-w-10 min-h-10 md:min-w-14 md:min-h-14 flex items-center justify-center bg-[#4867C8] text-white rounded-full"
            >
              <img :src="feature.icon" class="w-4 md:w-6 fill-white text-white" />
            </div>
            <div class="w-auto">
              <h3 class="text-sm md:text-base font-semibold text-[#061B44]">
                {{ feature.title }}
              </h3>
              <p class="text-gray-600 mt-1 text-xs md:text-sm">
                {{ feature.description }}
                <button
                  v-if="feature.link"
                  @click="scrollToSection(feature.link)"
                  class="text-[#4867C8] underline"
                >
                  {{ translated.here }}
                </button>
              </p>
            </div>
          </div>
        </div>
        <div
          class="absolute left-1/2 -top-10 min-w-20 min-h-20 flex items-center justify-center bg-[#4867C8] text-white rounded-full -translate-x-1/2"
        >
          <div class="flex flex-col items-center justify-center">
            <span>
              <svg
                class="w-6 h-6 text-white fill-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
              >
                <path
                  d="M12 1c-6.338 0-12 4.226-12 10.007 0 2.05.738 4.063 2.047 5.625.055 1.83-1.023 4.456-1.993 6.368 2.602-.47 6.301-1.508 7.978-2.536 9.236 2.247 15.968-3.405 15.968-9.457 0-5.812-5.701-10.007-12-10.007zm0 15c-.565 0-1.024-.459-1.024-1.025 0-.565.459-1.024 1.024-1.024.566 0 1.024.459 1.024 1.024 0 .566-.458 1.025-1.024 1.025zm1.606-4.858c-.74.799-.775 1.241-.766 1.785h-1.643c-.006-1.208.016-1.742 1.173-2.842.469-.446.84-.799.788-1.493-.047-.66-.599-1.004-1.117-1.004-.581 0-1.261.432-1.261 1.649h-1.646c0-1.966 1.155-3.237 2.941-3.237.849 0 1.592.278 2.09.783.468.473.709 1.125.7 1.883-.013 1.134-.704 1.878-1.259 2.476z"
                />
              </svg>
            </span>
            <span class="text-base font-semibold">{{ $t("help") }}</span>
          </div>
        </div>
        <div
          @click="showHelp = false"
          class="absolute right-0 top-0 bg-[#4867C8] p-2 rounded-bl-2xl rounded-tr-md cursor-pointer"
        >
          <img
            src="../assets/images/audio/close.svg"
            class="w-6 fill-white text-white invert"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.drag-drop-area {
  border: 2px dashed #ccc;
  padding: 20px;
  text-align: center;
  cursor: pointer;
}

.text-overlay {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 5px;
  z-index: 999;
}
.videoplay {
  width: 70%;
  height: 100%;
}
</style>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import { toast } from "vue3-toastify";
import { useAudioStore } from "../store/audioStore";
import AudioProcessingResult from "./modules/audio/audio_processing_result.vue";
import ProcessAudioFile from "./modules/audio/process_audio_file.vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
const { t, locale } = useI18n();
import tts from "../assets/images/help/tts.svg";
import eat from "../assets/images/help/eat.svg";
import mas from "../assets/images/help/mas.svg";
import ass from "../assets/images/help/as.svg";
import mlt from "../assets/images/help/mlt.svg";
import cfs from "../assets/images/help/cfs.svg";
import { useHead } from "@vueuse/head";

const as = useAudioStore();
const showHelp = ref(false);
const showNewProcessPop = ref(false);
const processAudioFileRef = ref<InstanceType<typeof ProcessAudioFile> | null>(null);
const router = useRouter();
const isProduction = import.meta.env.PROD;
const basePath = isProduction ? "/assets/images/" : "src/assets/images/";
const processingStarted = computed({
  get: () => as.processingStarted,
  set: (value) => (as.processingStarted = value),
});
const receivedCaption = computed({
  get: () => as.receivedCaption,
  set: (value) => (as.receivedCaption = value),
});
const originalText = computed({
  get: () => as.originalText,
  set: (value) => (as.originalText = value),
});
const translatedText = computed({
  get: () => as.translatedText,
  set: (value) => (as.translatedText = value),
});
const selectedAudio = computed({
  get: () => as.selectedAudio,
  set: (value) => (as.selectedAudio = value),
});
const userInputAudioLink = computed({
  get: () => as.userInputAudioLink,
  set: (value) => (as.userInputAudioLink = value),
});
const notReadyYet = computed({
  get: () => as.notReadyYet,
  set: (value) => (as.notReadyYet = value),
});
const isFromNotification = computed({
  get: () => as.isFromNotification,
  set: (value) => (as.isFromNotification = value),
});
const isTargetLanguageSelected = computed({
  get: () => as.isTargetLanguageSelected,
  set: (value) => (as.isTargetLanguageSelected = value),
});
const defaultOriginalText = computed({
  get: () => as.defaultOriginalText,
  set: (value) => (as.defaultOriginalText = value),
});
const defaultTranslatedText = computed({
  get: () => as.defaultTranslatedText,
  set: (value) => (as.defaultTranslatedText = value),
});
const originalAudioSubtitle = computed({
  get: () => as.originalAudioSubtitle,
  set: (value) => (as.originalAudioSubtitle = value),
});
const translatedAudioSubtitle = computed({
  get: () => as.translatedAudioSubtitle,
  set: (value) => (as.translatedAudioSubtitle = value),
});
const prebuildAudioSubtitle = computed({
  get: () => as.prebuildAudioSubtitle,
  set: (value) => (as.prebuildAudioSubtitle = value),
});
const defaultOriginalAudioSubtitle = computed({
  get: () => as.defaultOriginalAudioSubtitle,
  set: (value) => (as.defaultOriginalAudioSubtitle = value),
});
const defaultTranslatedAudioSubtitle = computed({
  get: () => as.defaultTranslatedAudioSubtitle,
  set: (value) => (as.defaultTranslatedAudioSubtitle = value),
});
const processingLink = computed({
  get: () => as.processingLink,
  set: (value) => (as.processingLink = value),
});

const processNewAudio = () => {
  try {
    receivedCaption.value = false;
    processingStarted.value = false;
    notReadyYet.value = false;
    originalText.value = "";
    translatedText.value = "";
    selectedAudio.value.name = "";
    userInputAudioLink.value = "";
    isFromNotification.value = false;
    isTargetLanguageSelected.value = false;
    defaultOriginalText.value = "";
    defaultTranslatedText.value = "";
    originalAudioSubtitle.value = "";
    translatedAudioSubtitle.value = "";
    prebuildAudioSubtitle.value = "";
    defaultOriginalAudioSubtitle.value = "";
    defaultTranslatedAudioSubtitle.value = "";
    processingLink.value = false;
    as.pendingHelpScrollSectionId = null;
    processAudioFileRef.value?.resetOneTimeState?.();
    router.push({
      name: "AudioTranscriptor",
    });
  } catch (error) {
    console.log(error);
  }
};
function scrollToSection(sectionId: string) {
  showHelp.value = false;
  nextTick(() => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
      as.pendingHelpScrollSectionId = null;
      return;
    }
    as.pendingHelpScrollSectionId = sectionId;
    toast(t("help-panel-scroll-after-transcription"), {
      autoClose: 4000,
      type: "info",
      theme: "light",
    });
  });
}

const translated = computed(() => ({
  title: t("welcome-to-the-transcription-panel-heres-what-you-can-do"),
  here: t("here"),
  close: t("close"),
  features: [
    {
      title: t("ai-text-to-speech-feature"),
      description: t("use-the-ai-powered-text-to-speech-feature"),
      link: "ttsPanel",
      icon: tts,
    },
    {
      title: t("edit-and-translate-audio-text"),
      description: t("explore-and-edit-the-text-extracted-from-your"),
      link: "textPanel",
      icon: eat,
    },
    {
      title: t("manage-audio-subtitles"),
      description: t("you-can-explore-edit-and"),
      link: "srtPanel",
      icon: mas,
    },
    {
      title: t("auto-saving"),
      description: t(
        "updates-to-text-or-subtitles-are-saved-automatically-ensuring-you"
      ),
      icon: ass,
    },
    {
      title: t("multi-language-translation"),
      description: t(
        "translate-the-video-text-into-as-many-languages-as-you-like"
      ),
      icon: mlt,
    },
    {
      title: t("customize-font-size-and-style"),
      description: t(
        "adjust-the-font-size-and-family-for-the-text-and-subtitles-easily-from"
      ),
      link: "fontPanel",
      icon: cfs,
    },
  ],
}));

// Title is set by the route/page component, not by this reusable component
</script>
